import { GoogleCalendarEvent } from '../interfaces/google-calendar-event';
import { notify } from './notify';

export function luminance(r: number, g: number, b: number) {
  var a = [r, g, b].map(function (v) {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function contrast(rgb1: number[], rgb2: number[]) {
  var lum1 = luminance(rgb1[0], rgb1[1], rgb1[2]);
  var lum2 = luminance(rgb2[0], rgb2[1], rgb2[2]);
  var brightest = Math.max(lum1, lum2);
  var darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

export function hexToRgb(hex: string) {
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
}

export function jsonCompare(value: any, value2: any): any {
  return JSON.stringify(value) === JSON.stringify(value2);
}

export function base64ToBlob(base64: string, contentType: string = ''): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays as unknown as BlobPart[], { type: contentType });
}

export function dataURLtoBlob(dataURL: any) {
  // Split the data URL to get the mime type and the base64 data
  const [header, base64Data] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];

  // Decode the base64 data
  const decodedData = atob(base64Data);

  // Create a Uint8Array from the decoded data
  const dataArray = new Uint8Array(decodedData.length);
  for (let i = 0; i < decodedData.length; i++) {
    dataArray[i] = decodedData.charCodeAt(i);
  }

  // Create and return the Blob
  return new Blob([dataArray], { type: mime });
}

export function arrayRange(start: number, stop: number, step: number) {
  return Array.from(
    { length: (stop - start) / step + 1 },
    (value, index) => start + index * step,
  );
}

export function sortObjectKeys(obj: any) {
  const sortedEntries = Object.entries(obj).sort(([keyA], [keyB]) =>
    keyA.localeCompare(keyB),
  );
  // Remove all current properties
  for (const key in obj) {
    delete obj[key];
  }

  // Add back sorted properties
  for (const [key, value] of sortedEntries) {
    obj[key] = value;
  }

  return obj; // optional, since obj is modified in place
}

export function removeLocalStorageItems() {
  var storageKeys = Object.keys(localStorage).map((key) => key);

  storageKeys.forEach((element) => {
    if (
      'orderInfo|refresh-token|sidebar-locked|login-image|department|department-description|persistent-drawer'.indexOf(
        element,
      ) == -1 &&
      element.indexOf('calendar-view') == -1 &&
      !element.includes('sia-theme') &&
      !element.startsWith('NODELETE')
    ) {
      localStorage.removeItem(element);
    }
  });
}

/**
 * Gets the appropriate icon, color, and preview capability for a document based on its filename
 * @param fileName The name of the file (including extension)
 * @returns Object containing icon name, color hex code, and preview capability
 */
export function getDocumentIcon(
  fileName?: string,
  fileType?: string,
): {
  icon: string;
  color: string;
  canPreview: boolean;
} {
  if (!fileName && !fileType) {
    return { icon: 'description', color: '#6B7280', canPreview: false }; // gray
  }

  const extension =
    fileType?.toLowerCase()?.replace('.', '') ||
    fileName?.toLowerCase().split('.').pop();

  switch (extension) {
    case 'pdf':
      return { icon: 'picture_as_pdf', color: '#DC2626', canPreview: true }; // red
    case 'xlsx':
    case 'xls':
    case 'csv':
      return { icon: 'table_chart', color: '#059669', canPreview: false }; // green
    case 'docx':
    case 'doc':
      return { icon: 'article', color: '#2563EB', canPreview: true }; // blue
    case 'pptx':
    case 'ppt':
      return { icon: 'slideshow', color: '#D97706', canPreview: true }; // orange
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return { icon: 'image', color: '#7C3AED', canPreview: true }; // purple
    case 'txt':
    case 'md':
    case 'rtf':
      return { icon: 'text_snippet', color: '#6B7280', canPreview: true }; // gray
    case 'msg':
    case 'eml':
      return { icon: 'mail', color: '#6e93de', canPreview: false }; // blue
    default:
      return { icon: 'description', color: '#6B7280', canPreview: false }; // gray
  }
}
export function getFileTypeAllowed(
  fileType: string | undefined,
  filename: string | undefined,
): string {
  if (!filename) return 'unknown';

  // Accept either a filename (e.g. "file.png") or a MIME type (e.g. "image/png").
  let extCandidate = filename.toLowerCase();
  let ext =
    fileType?.toLowerCase()?.replace('.', '') || extCandidate.split('.').pop();
  if (!ext || ext.includes('/')) {
    ext = extCandidate.split('/').pop() || ext;
  }
  switch (ext) {
    case 'pdf':
      return 'pdf';
    case 'xls':
      return 'xls';
    case 'xlsx':
      return 'xlsx';
    case 'doc':
      return 'doc';
    case 'docx':
      return 'docx';
    case 'eml':
      return 'eml';
    case 'msg':
      return 'msg';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
    case 'svg':
    case 'ico':
    case 'tiff':
    case 'tif':
    case 'avif':
    case 'heic':
    case 'heif':
      return ext;
    case 'txt':
    case 'xml':
    case 'json':
    case 'csv':
    case 'yaml':
    case 'yml':
    case 'log':
    case 'sql':
    case 'ini':
    case 'md':
      return 'text';
    default:
      return '';
  }
}

export function nt(ending: boolean = false) {
  return ending ? '{{/nt}}' : '{{nt}}';
}

export function copyToClipboard(text: string, message?: string) {
  navigator.clipboard.writeText(text);
  notify(message ?? 'Testo copiato', 1500, 'info', 'content_copy');
}
export function generateGoogleCalendarLink(
  eventDto: GoogleCalendarEvent,
): string {
  const baseUrl = 'https://calendar.google.com/calendar/render';

  // Helper to strip dashes, colons, and milliseconds from ISO strings
  // Example: "2026-04-15T10:00:00.000Z" -> "20260415T100000Z"
  const formatToUtcString = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().replace(/-|:|\.\d{3}/g, '');
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventDto.title,
    dates: `${formatToUtcString(eventDto.startTime)}/${formatToUtcString(eventDto.endTime)}`,
  });

  if (eventDto.description) {
    params.append('details', eventDto.description);
  }

  if (eventDto.location) {
    params.append('location', eventDto.location);
  }

  // URLSearchParams automatically applies the correct URL encoding (similar to Uri.EscapeDataString)
  return `${baseUrl}?${params.toString()}`;
}
